import * as React from 'react';
import type { MapRef } from 'react-map-gl';
import MapBoxMap, { Marker, NavigationControl, Popup } from 'react-map-gl';

import { Maximize2 } from 'lucide-react';

import { env } from '@/constants/env';
import { cn } from '@/lib/utils';
import { useModal } from '../modals/new/use-modal';
import { MapFullscreenModal } from '../modals/MapFullscreenModal';

const MAP_BOX_TOKEN = env.MAP_BOX_API_TOKEN;

export interface MapProps extends React.PropsWithChildren {
  longitude?: number | null;
  latitude?: number | null;
  zoom?: number;
  bearing?: number;
  pitch?: number;
  popupInfo?: React.ReactNode;
  fullscreenControl?: boolean;
  navigationControl?: boolean;
  preserveDrawingBuffer?: boolean;
}

const Icon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width="29"
    height="47"
    viewBox="0 0 29 47"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M14.5 0C6.48929 0 0 6.57203 0 14.6849C0 29.3698 14.5 47 14.5 47C14.5 47 29 29.3802 29 14.6849C29 6.57203 22.5107 0 14.5 0ZM14.5 23.6782C9.59422 23.6782 5.61991 19.6532 5.61991 14.6849C5.61991 9.71655 9.59422 5.69157 14.5 5.69157C19.4058 5.69157 23.3801 9.71655 23.3801 14.6849C23.3801 19.6532 19.4058 23.6782 14.5 23.6782ZM8.88009 14.6849C8.88009 17.8294 11.3951 20.3764 14.5 20.3764C17.6049 20.3764 20.1199 17.8294 20.1199 14.6849C20.1199 11.5404 17.6049 8.99331 14.5 8.99331C11.3951 8.99331 8.88009 11.5404 8.88009 14.6849Z"
      fill="#07B1BE"
    />
  </svg>
);

export const MapBox = React.forwardRef<MapRef, MapProps>(
  (
    {
      zoom: propsZoom = 14,
      bearing = 0,
      pitch = 0,
      popupInfo,
      children,
      preserveDrawingBuffer,
      fullscreenControl = true,
      navigationControl = true,
      ...positionProps
    },
    ref
  ) => {
    const [openedPlacePopup, setOpenedPlacePopup] = React.useState(false);

    const showPopupInfo = () => {
      if (popupInfo) setOpenedPlacePopup(true);
    };
    const closePopupInfo = () => setOpenedPlacePopup(true);
    const modal = useModal();

    return (
      <>
        <div
          className={cn(
            'size-full',
            '[&_.mapboxgl-ctrl-group]:top-[50%] [&_.mapboxgl-ctrl-group]:flex [&_.mapboxgl-ctrl-group]:flex-col [&_.mapboxgl-ctrl-group]:gap-2 [&_.mapboxgl-ctrl-group]:bg-transparent [&_.mapboxgl-ctrl-group]:shadow-none',
            '[&_.mapboxgl-ctrl-shrink]:size-8 [&_.mapboxgl-ctrl-shrink]:!rounded-sm [&_.mapboxgl-ctrl-shrink]:bg-primary-light',
            '[&_.mapboxgl-ctrl-fullscreen]:size-8 [&_.mapboxgl-ctrl-fullscreen]:!rounded-sm [&_.mapboxgl-ctrl-fullscreen]:bg-primary-light',
            '[&_.mapboxgl-ctrl-zoom-in]:size-8 [&_.mapboxgl-ctrl-zoom-in]:!rounded-sm [&_.mapboxgl-ctrl-zoom-in]:bg-primary-light',
            '[&_.mapboxgl-ctrl-zoom-out]:size-8 [&_.mapboxgl-ctrl-zoom-out]:!rounded-sm [&_.mapboxgl-ctrl-zoom-out]:bg-primary-light',
            '[&_.mapboxgl-ctrl-bottom-right]:hidden [&_.mapboxgl-ctrl-compass]:hidden [&_.mapboxgl-ctrl-scale]:hidden [&_a.mapboxgl-ctrl-logo]:hidden'
          )}
        >
          {!Number.isNaN(Number(positionProps.latitude)) &&
          !Number.isNaN(Number(positionProps.longitude)) ? (
            <MapBoxMap
              ref={ref}
              initialViewState={{
                longitude: Number(positionProps.longitude),
                latitude: Number(positionProps.latitude),
                zoom: propsZoom,
                bearing,
                pitch,
              }}
              mapStyle="mapbox://styles/mapbox/light-v9"
              mapboxAccessToken={MAP_BOX_TOKEN}
              preserveDrawingBuffer={preserveDrawingBuffer}
              style={{ height: '100%', width: '100%' }}
            >
              {fullscreenControl ? (
                <div
                  className="pointer-events-none absolute left-2.5 top-1/3 z-[5] -translate-y-[40px]"
                  data-testid="fullscreen-trigger-wrapper"
                >
                  <button
                    type="button"
                    aria-label="Open map in modal"
                    onClick={modal.open}
                    data-testid="open-map-modal"
                    className="pointer-events-auto inline-grid size-8 place-items-center !rounded-sm bg-primary-light shadow-none"
                  >
                    <Maximize2 className="h-5 w-5" />
                  </button>
                </div>
              ) : null}
              {navigationControl ? <NavigationControl position="top-left" /> : null}
              <Marker
                longitude={Number(positionProps.longitude)}
                latitude={Number(positionProps.latitude)}
                anchor="bottom"
                onClick={showPopupInfo}
              >
                <Icon className={cn(popupInfo && 'cursor-pointer')} />
              </Marker>
              {openedPlacePopup ? (
                <Popup
                  longitude={Number(positionProps.longitude)}
                  latitude={Number(positionProps.latitude)}
                  anchor="top"
                  onClose={closePopupInfo}
                >
                  {popupInfo}
                </Popup>
              ) : null}
              {children}
            </MapBoxMap>
          ) : null}
        </div>
        <MapFullscreenModal
          modal={modal}
          latitude={positionProps.latitude}
          longitude={positionProps.longitude}
          zoom={propsZoom}
          bearing={bearing}
          pitch={pitch}
          popupInfo={popupInfo}
        >
          {children}
        </MapFullscreenModal>
      </>
    );
  }
);
